/**
 * PATH: packages/bills/src/components/BillLineAllocationGrid.tsx
 * FILE_ID: BILLS-PKG-CMP-ALLOC-GRID-001
 * PURPOSE: Per-line allocation grid (p3b) — a clay/excel coding surface
 *          where each bill line gets Account (CoA) + Sub-account + Cost
 *          category + Job + Tax. Edits the SAME ``line_items`` array the
 *          bill PATCH already persists (JSON field — no migration).
 *
 * WHY: Pushing a bill to Xero needs every line coded to a real GL
 *      account; coding per line (vs one account for the whole bill) is
 *      what lets the CFO rollups attribute cost correctly. This grid is
 *      the operator's coding workbench, and the coverage footer tells
 *      them at a glance how many lines are still uncoded before they push.
 *
 * FLOW:
 *   line_items[] ─▶ row per line ─▶ edit cell ─▶ onChange(next line_items)
 *                                              └▶ host PATCHes the bill.
 *
 * DEPENDENCIES: react + ../types + ../coaOptions (pure grouping). The CoA
 *               account list is passed in (fetched by the host via the
 *               injected client) so this component stays transport-free.
 */

import { useMemo } from 'react';
import type { BillLineItem, CoaAccount } from '../types';
import {
  COST_CATEGORY_OPTIONS,
  TAX_TYPE_OPTIONS,
  buildCoaOptionGroups,
} from '../coaOptions';

interface Props {
  items: BillLineItem[];
  onChange: (items: BillLineItem[]) => void;
  /** Org CoA for the picker; null/empty → account cell degrades to free text. */
  accounts?: CoaAccount[] | null;
  /** Loading state for the CoA fetch (shows a hint instead of an empty picker). */
  accountsLoading?: boolean;
  disabled?: boolean;
}

// Per-line coding is rendered as a STACKED CARD (not a wide excel grid) so it
// fits the narrow detail pane without horizontal scroll / clipped columns.
// Each field is a small labelled control on an h-8 bordered input.
const cell =
  'h-8 w-full rounded-md border border-border bg-background px-2 text-xs text-foreground outline-none focus:border-primary focus:ring-1 focus:ring-primary/40 disabled:cursor-not-allowed disabled:opacity-50';

const fieldLabel =
  'mb-0.5 block text-[10px] font-medium uppercase tracking-wide text-muted-foreground';

/** First non-empty of the line's coding/legacy keys. */
function lineCode(it: BillLineItem): string {
  return (
    (it.account_code as string) ||
    (it.AccountCode as string) ||
    ''
  );
}

export function BillLineAllocationGrid({
  items,
  onChange,
  accounts,
  accountsLoading,
  disabled,
}: Props) {
  const optionGroups = useMemo(
    () => buildCoaOptionGroups(accounts ?? []),
    [accounts],
  );
  const hasCoa = optionGroups.length > 0;

  // Coverage: a line counts as "coded" once it has a GL account.
  const codedCount = items.filter((it) => lineCode(it).trim()).length;
  const allCoded = items.length > 0 && codedCount === items.length;

  function updateAt(idx: number, patch: Partial<BillLineItem>) {
    const next = items.slice();
    next[idx] = { ...next[idx], ...patch };
    onChange(next);
  }

  return (
    <details className="mb-2.5 rounded-lg border border-border bg-card shadow-sm" open>
      <summary className="flex cursor-pointer select-none items-center justify-between gap-2 px-3 py-2 text-xs font-semibold tracking-tight text-foreground">
        <span>Allocation &amp; coding</span>
        {items.length > 0 && (
          <span
            className={[
              'rounded-full px-1.5 py-0.5 text-[11px] font-medium',
              allCoded
                ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300'
                : 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
            ].join(' ')}
            title="Lines with a GL account assigned"
          >
            {codedCount}/{items.length} coded
          </span>
        )}
      </summary>

      {items.length === 0 ? (
        <p className="border-t border-border px-3 py-2 text-xs italic text-muted-foreground">
          No line items to allocate.
        </p>
      ) : (
        <ul className="divide-y divide-border border-t border-border">
          {items.map((it, idx) => (
            <li key={idx} className="space-y-2 px-3 py-2.5">
              {/* Line header — description + line total. */}
              <div className="flex items-baseline justify-between gap-2">
                <span
                  className="min-w-0 truncate text-xs font-medium text-foreground"
                  title={it.description || ''}
                >
                  {it.description || (
                    <span className="italic text-muted-foreground">Line {idx + 1}</span>
                  )}
                </span>
                <span className="shrink-0 text-xs font-medium tabular-nums text-foreground">
                  {fmtAmount(it.line_total)}
                </span>
              </div>

              {/* Coding fields — a compact 2-col form that never overflows the
                  pane. Account (CoA) spans the full width; the rest pair up. */}
              <div className="grid grid-cols-2 gap-x-2 gap-y-2">
                <label className="col-span-2 block">
                  <span className={fieldLabel}>Account (CoA)</span>
                  {hasCoa ? (
                    <select
                      className={cell}
                      disabled={disabled}
                      value={lineCode(it)}
                      onChange={(e) => updateAt(idx, { account_code: e.target.value || null })}
                    >
                      <option value="">{accountsLoading ? 'Loading…' : '—'}</option>
                      {optionGroups.map((g) => (
                        <optgroup key={g.label} label={g.label}>
                          {g.options.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  ) : (
                    <input
                      className={cell}
                      disabled={disabled}
                      placeholder={accountsLoading ? 'Loading…' : 'Account code'}
                      value={lineCode(it)}
                      onChange={(e) => updateAt(idx, { account_code: e.target.value || null })}
                    />
                  )}
                </label>

                <label className="block">
                  <span className={fieldLabel}>Sub-acct</span>
                  <input
                    className={cell}
                    disabled={disabled}
                    placeholder="—"
                    value={(it.sub_account_code as string) ?? ''}
                    onChange={(e) => updateAt(idx, { sub_account_code: e.target.value || null })}
                  />
                </label>

                <label className="block">
                  <span className={fieldLabel}>Cost category</span>
                  <select
                    className={cell}
                    disabled={disabled}
                    value={(it.cost_category as string) ?? ''}
                    onChange={(e) => updateAt(idx, { cost_category: e.target.value || null })}
                  >
                    {COST_CATEGORY_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="block">
                  <span className={fieldLabel}>Job</span>
                  <input
                    className={cell}
                    disabled={disabled}
                    placeholder="Job #"
                    value={(it.job_code as string) ?? ''}
                    onChange={(e) => updateAt(idx, { job_code: e.target.value || null })}
                  />
                </label>

                <label className="block">
                  <span className={fieldLabel}>Tax</span>
                  <select
                    className={cell}
                    disabled={disabled}
                    value={(it.tax_type as string) ?? ''}
                    onChange={(e) => updateAt(idx, { tax_type: e.target.value || null })}
                  >
                    {TAX_TYPE_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </li>
          ))}
        </ul>
      )}

      {!hasCoa && !accountsLoading && (
        <p className="px-2 py-1.5 text-[11px] text-muted-foreground">
          Connect Xero / sync the Chart of Accounts to pick GL accounts from a
          list. You can still type an account code above.
        </p>
      )}
    </details>
  );
}

function fmtAmount(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(Number(v))) return '—';
  return Number(v).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}
